import { useEffect, useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import type { ContentIndex, MediaRef } from '@ezishizi/content-schema';
import { loadIndex, loadLesson } from '../lib/content.ts';
import { Shizi } from '../components/mascot/Shizi.tsx';
import { Picture } from '../components/ui.tsx';
import { MicIcon, SpeakerIcon } from '../components/icons.tsx';

type DemoWord = { id: string; hanzi: string; media?: MediaRef };

const DEMO_WORDS: DemoWord[] = [
  { id: 'yu-rain', hanzi: '雨' },
  { id: 'yu-fish', hanzi: '鱼' },
  { id: 'ma-horse', hanzi: '马' },
  { id: 'yifu', hanzi: '衣服' },
];

function Step({ n, title, body }: { n: number; title: string; body: string }) {
  return (
    <div className="flex flex-col items-start gap-3">
      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-soft text-xl font-bold text-brand-deep">
        {n}
      </span>
      <h3 className="text-xl font-bold">{title}</h3>
      <p className="text-muted">{body}</p>
    </div>
  );
}

function SkillCard({ sample, title, body }: { sample: ReactNode; title: string; body: string }) {
  return (
    <div className="flex flex-col gap-4 rounded-3xl bg-white p-6 shadow-sm">
      {sample}
      <h3 className="text-xl font-bold">{title}</h3>
      <p className="text-muted">{body}</p>
    </div>
  );
}

function DemoCard() {
  const [words, setWords] = useState(DEMO_WORDS);

  useEffect(() => {
    let cancelled = false;
    void loadLesson('p1a-l01')
      .then((bundle) => {
        if (cancelled) return;
        setWords((prev) =>
          prev.map((w) => {
            const word = bundle.words[w.id];
            const media = word?.imageId ? bundle.media[word.imageId] : undefined;
            return media ? { ...w, media } : w;
          }),
        );
      })
      .catch(() => {
        // no bundle yet (or offline): the tiles stay as blank placeholders
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const rain = words[0]!;

  return (
    <div
      className="relative rounded-[32px] bg-white p-6 shadow-lg sm:p-8"
      role="img"
      aria-label="A demo of a listening exercise: hear the word yǔ (rain) and tap the matching picture, shown correct."
    >
      <Shizi pose="cheer" size={72} className="absolute -right-4 -top-6 sm:-right-6 sm:-top-8" />
      <span className="inline-flex items-center gap-3 rounded-full bg-paper px-4 py-2 text-lg text-muted">
        <span>Listen, then tap the picture</span>
      </span>
      <div className="mt-6 flex items-center gap-4">
        <div className="flex h-16 w-16 flex-none items-center justify-center rounded-full bg-brand">
          <SpeakerIcon className="h-8 w-8 text-white" />
        </div>
        <div>
          <span className="hanzi text-6xl">{rain.hanzi}</span>
          <p className="text-muted">yǔ · rain</p>
        </div>
      </div>
      <div className="mt-6 grid grid-cols-2 gap-3">
        {words.map((w, i) => (
          <div
            key={w.id}
            className="tile text-5xl sm:text-6xl"
            aria-hidden
            data-state={i === 0 ? 'correct' : undefined}
          >
            {w.media ? (
              <Picture media={w.media} />
            ) : (
              <span className="block h-[1em] w-[1em] rounded-2xl bg-paper" />
            )}
          </div>
        ))}
      </div>
      <div className="pop mt-6 inline-flex items-center gap-2 rounded-full bg-ok px-6 py-2 text-lg font-bold text-white">
        <span>✓</span>
        <span>Great!</span>
      </div>
    </div>
  );
}

export function Landing() {
  const [index, setIndex] = useState<ContentIndex | null>(null);

  useEffect(() => {
    void loadIndex()
      .then(setIndex)
      .catch(() => {
        // landing still works without the index: the lesson/level rows are just omitted
      });
  }, []);

  const levels = index ? [...index.levels].sort((a, b) => a.order - b.order) : [];
  const availableLevelIds = new Set(
    (index?.lessons ?? []).filter((l) => l.url).map((l) => l.levelId),
  );
  const sampleLessons = (index?.lessons ?? [])
    .filter((l) => l.levelId === 'p1' && l.title)
    .sort((a, b) => a.order - b.order)
    .slice(0, 6);

  return (
    <div className="mx-auto max-w-5xl px-6 py-6">
      <header className="flex items-center justify-between gap-4 py-4">
        <div className="flex items-center gap-3">
          <Shizi size={40} pose="still" />
          <div>
            <span className="display block text-2xl">Ezi Shizi</span>
            <span className="block text-sm text-muted">Singapore Primary Chinese</span>
          </div>
        </div>
        <Link
          to="/auth"
          className="btn btn-ghost text-base min-h-0 py-3"
          data-testid="landing-sign-in"
        >
          Sign in
        </Link>
      </header>

      <main className="flex flex-col gap-24 py-8">
        <section className="grid items-center gap-12 lg:grid-cols-2">
          <div className="flex flex-col items-start gap-6">
            <h1 className="display text-5xl lg:text-6xl">Chinese practice kids actually enjoy.</h1>
            <p className="text-xl text-muted">
              Visual, focused practice for listening, speaking, reading and writing, built around
              Singapore Primary Chinese.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/auth" className="btn btn-primary" data-testid="landing-get-started">
                Start learning
              </Link>
              <a href="#how" className="btn btn-ghost">
                See how it works
              </a>
            </div>
            <p className="text-lg">Easy for children to use. Easy for parents to follow.</p>
            <p className="text-sm text-muted">
              Works in the browser on iPad, tablet and laptop. Nothing to install.
            </p>
          </div>
          <DemoCard />
        </section>

        <section className="rounded-[32px] bg-white p-8 shadow-sm" data-testid="why-shizi">
          <div className="grid items-center gap-8 lg:grid-cols-[auto_1fr]">
            <Shizi pose="still" size={120} />
            <div className="flex flex-col gap-4">
              <h2 className="display text-3xl">Why Shizi?</h2>
              <p className="text-lg text-muted">
                Ezi is easy. Shizi sounds like the Chinese for lion, a nod to the Lion City and to
                our mascot, and it is close to the word for learning to read characters. Most
                children just call it the lion game.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="block rounded-2xl bg-paper px-5 py-4">
                  <span className="hanzi text-4xl">狮子</span>
                  <p className="text-muted">shīzi · lion</p>
                </div>
                <div className="block rounded-2xl bg-paper px-5 py-4">
                  <span className="hanzi text-4xl">识字</span>
                  <p className="text-muted">shízì · learning to read characters</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="how" className="flex flex-col gap-10">
          <h2 className="display text-3xl">How it works</h2>
          <div className="grid gap-8 sm:grid-cols-3">
            <Step
              n={1}
              title="Sign in and add your child"
              body="Create a parent account with Google or an email link, then add a profile for each child."
            />
            <Step
              n={2}
              title="Pick their school level"
              body="Choose Primary 1 to 6 and the lesson they're on at school. Practice starts right there."
            />
            <Step
              n={3}
              title="Practise a little, often"
              body="Short activities with pictures and sound. A lesson takes minutes, and the app is simple enough for a child to use on their own."
            />
          </div>
        </section>

        <section className="flex flex-col gap-10">
          <h2 className="display text-3xl">Listen. Say it. Read. Write.</h2>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <SkillCard
              sample={
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-brand">
                  <SpeakerIcon className="h-7 w-7 text-white" />
                </div>
              }
              title="Listen"
              body="Hear a word and tap the matching picture. Pictures come before text, so meaning clicks first."
            />
            <SkillCard
              sample={
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-accent">
                  <MicIcon className="h-7 w-7 text-white" />
                </div>
              }
              title="Say it"
              body="Repeat after the model. The app listens and shows how it went."
            />
            <SkillCard
              sample={
                <div>
                  <span className="hanzi text-4xl">鱼</span>
                  <p className="text-sm text-muted">yú · fish</p>
                </div>
              }
              title="Read"
              body="Recognise characters and complete sentences from the lesson."
            />
            <SkillCard
              sample={
                <div>
                  <span className="hanzi text-4xl">马</span>
                  <p className="text-sm text-muted">mǎ · horse</p>
                </div>
              }
              title="Write"
              body="Trace each character stroke by stroke, in the right order."
            />
          </div>
        </section>

        <section className="grid gap-10 lg:grid-cols-2">
          <div className="flex flex-col gap-4">
            <h2 className="display text-3xl">Made for families who don't speak Chinese at home</h2>
            <p className="text-muted text-xl">
              Ezi Shizi uses clear visuals, English parent navigation and audio-first learning, so
              children can practise independently even when Mum or Dad cannot read Chinese.
            </p>
          </div>
          <ul className="flex flex-col gap-4">
            {[
              {
                title: 'Practise what your child is actually learning',
                body: 'Lessons follow the school sequence, so home practice matches class.',
              },
              {
                title: 'Built for independent use',
                body: 'Designed so children can get started without a parent translating every screen.',
              },
              {
                title: 'Whole-language practice',
                body: 'Listening, speaking, reading, vocabulary and writing, not just flashcards.',
              },
            ].map((item) => (
              <li key={item.title} className="flex items-start gap-3 text-lg">
                <span className="text-ok" aria-hidden>
                  ✓
                </span>
                <span>
                  <span className="block font-bold">{item.title}</span>
                  <span className="block text-muted">{item.body}</span>
                </span>
              </li>
            ))}
          </ul>
        </section>

        <section className="flex flex-col gap-8">
          <h2 className="display text-3xl">Built around Singapore Primary Chinese</h2>
          <p className="text-xl text-muted">
            Lessons follow the Primary Chinese character lists, so practice matches what's being
            taught at school, lesson by lesson, in the same order.
          </p>
          {sampleLessons.length > 0 && (
            <div className="flex flex-wrap gap-3">
              {sampleLessons.map((l) => (
                <div
                  key={l.id}
                  className="flex items-center gap-2 rounded-2xl bg-white px-4 py-3 shadow-sm"
                >
                  <span className="hanzi text-2xl">{l.title!.zh}</span>
                  <span className="text-muted">{l.title!.en}</span>
                </div>
              ))}
            </div>
          )}
          {levels.length > 0 && (
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap gap-3">
                {levels.map((l) => {
                  const available = availableLevelIds.has(l.id);
                  return (
                    <span
                      key={l.id}
                      className={
                        available
                          ? 'rounded-2xl bg-brand-soft px-4 py-2 text-lg font-semibold text-brand-deep ring-2 ring-brand'
                          : 'rounded-2xl border border-dashed border-black/20 px-4 py-2 text-lg font-semibold text-muted'
                      }
                    >
                      {l.id.toUpperCase()}
                    </span>
                  );
                })}
              </div>
              {(() => {
                const sortedIds = levels.map((l) => l.id);
                const availableSorted = sortedIds.filter((id) => availableLevelIds.has(id));
                const unavailable = sortedIds.filter((id) => !availableLevelIds.has(id));
                if (availableSorted.length === 0 || unavailable.length === 0) return null;
                const isPrefix = sortedIds
                  .slice(0, availableSorted.length)
                  .every((id) => availableLevelIds.has(id));
                if (!isPrefix) return null;
                const readyLabel = availableSorted.map((id) => id.toUpperCase()).join(', ');
                const comingFirst = unavailable[0]!.toUpperCase();
                const comingLast = unavailable[unavailable.length - 1]!.toUpperCase();
                const comingLabel =
                  unavailable.length > 1 ? `${comingFirst} to ${comingLast}` : comingFirst;
                return (
                  <p className="text-sm text-muted">
                    {readyLabel} {availableSorted.length > 1 ? 'are' : 'is'} ready today.{' '}
                    {comingLabel} {unavailable.length > 1 ? 'are' : 'is'} being added.
                  </p>
                );
              })()}
            </div>
          )}
        </section>

        <section className="flex flex-col items-center gap-6 rounded-[32px] bg-brand px-8 py-12 text-center text-white sm:flex-row sm:justify-between sm:text-left">
          <div className="flex flex-col items-center gap-4 sm:flex-row">
            <Shizi pose="idle" size={96} />
            <div>
              <p className="display text-4xl">Make Chinese Ezi.</p>
              <p className="mt-2 text-lg text-white/90">
                Set up a parent account and add your child in under a minute.
              </p>
            </div>
          </div>
          <Link
            to="/auth"
            className="btn bg-white text-brand-deep"
            style={{ boxShadow: '0 5px 0 0 var(--color-brand-deep)' }}
          >
            Start learning
          </Link>
        </section>
      </main>

      <footer className="flex flex-col items-center gap-2 py-8 text-center text-sm text-muted">
        <p>
          © 2026 Ezi Shizi ·{' '}
          <Link to="/auth" className="underline">
            Sign in
          </Link>
        </p>
        <p>
          Ezi Shizi is an independent product and is not affiliated with or endorsed by Singapore's
          Ministry of Education.
        </p>
      </footer>
    </div>
  );
}
